import { Vector2 } from "../../common/vector2.js";
import { Game } from "../../game.js";
import { ImgID } from "../../image_loader/img_id.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { ColorScheme } from "./color_scheme.js";
import { TextStyleScheme } from "./text_style_scheme.js";
import { UIElementHolder } from "./ui_element_holder.js";
import { UIImage } from "./ui_image.js";
import { UINineSliceElement } from "./ui_nine_slice_element.js";
import { UITextElement } from "./ui_text_element.js";

/** The coin balance as every counter shows it: thousands split by spaces ("10 000"). */
export function formatCoinCount( value: number | string ): string
{
  return String( value ).replace( /\B(?=(\d{3})+(?!\d))/g, " " );
}

/** Inverse of formatCoinCount (NaN when the text is not a number). */
export function parseCoinCount( text: string ): number
{
  return Number.parseInt( text.replace( /\s/g, "" ), 10 );
}

/** Sizes of a top-bar counter pill (coins and hearts), in layout px. */
export interface TopCounterMetrics
{
  bg_w: number;
  bg_h: number;
  /** Currency icon, relative to the pill background. It sticks out to the left. */
  icon_x: number;
  icon_y: number;
  icon_w: number;
  icon_h: number;
  /** Green "+", relative to the icon. */
  plus_x: number;
  plus_y: number;
  plus_w: number;
  plus_h: number;
  /** Counter text, relative to the pill background. */
  count_x: number;
  count_y: number;
  count_w: number;
  count_h: number;
  font_size: number;
  /** Smallest font a long balance may shrink to ("1 000 000" fits at about 33 design px). */
  min_font_size: number;
  /** Box around background + icon, relative to the pill background (left/top are <= 0). */
  holder_left: number;
  holder_top: number;
  holder_w: number;
  holder_h: number;
}

/**
 * The one place that knows where the top-bar counters sit. Every copy of the coin
 * counter (menu, overlays, dialogs, the coin-flight HUD) is built from this, so all
 * of them land on the same pixels and a hand-off from one to another is invisible.
 */
export class TopCounterLayout
{
  /** Left edge of the coin pill background, relative to the content origin (inside the safe area). */
  static coinX(): number { return LayoutScaler.xFromLayouts( 620, 835 ); }

  static heartX(): number { return LayoutScaler.xFromLayouts( 285, 430 ); }

  static y(): number { return LayoutScaler.yFromDesign( 122 ); }

  static metrics(): TopCounterMetrics
  {
    const bg_w = LayoutScaler.widthFromDesign( 310 );
    const bg_h = LayoutScaler.heightFromDesign( 100 );
    const icon_w = LayoutScaler.widthFromDesign( 174 );
    const icon_h = LayoutScaler.heightFromDesign( 174 );
    const icon_x = LayoutScaler.xFromDesign( -35 );
    const icon_y = bg_h / 2 - icon_h / 2;
    const plus_w = LayoutScaler.widthFromDesign( 70 );
    const plus_h = LayoutScaler.heightFromDesign( 70 );
    // The text zone runs from the coin's edge to just short of the pill's right
    // end; long balances shrink into it (createCountText) instead of wrapping.
    // Design px within the 310-wide pill: 128 is past the coin's rim, 294 is
    // where the pill's rounded end starts.
    const count_w = LayoutScaler.widthFromDesign( 166 );
    const count_right_pad = LayoutScaler.widthFromDesign( 16 );

    const holder_left = Math.min( 0, icon_x );
    const holder_top = Math.min( 0, icon_y );

    return {
      bg_w,
      bg_h,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      plus_x: icon_w - plus_w - LayoutScaler.xFromDesign( 10 ),
      plus_y: icon_h - plus_h - LayoutScaler.yFromDesign( 20 ),
      plus_w,
      plus_h,
      count_x: bg_w - count_w - count_right_pad,
      count_y: -LayoutScaler.yFromDesign( 5 ),
      count_w,
      count_h: bg_h,
      font_size: LayoutScaler.textSizeFromDesign( 45 ),
      min_font_size: LayoutScaler.textSizeFromDesign( 26 ),
      holder_left,
      holder_top,
      holder_w: Math.max( bg_w, icon_x + icon_w ) - holder_left,
      holder_h: Math.max( bg_h, icon_y + icon_h ) - holder_top,
    };
  }

  /** Centre of the coin icon in screen coordinates — where flying coins land. */
  static coinIconCenter(): Vector2
  {
    const m = TopCounterLayout.metrics();
    return new Vector2(
      Game.safeAreaLeft() + TopCounterLayout.coinX() + m.icon_x + m.icon_w / 2,
      Game.safeAreaTop() + TopCounterLayout.y() + m.icon_y + m.icon_h / 2
    );
  }

  static createCountText( ctx: CanvasRenderingContext2D, m: TopCounterMetrics, text: string ): UITextElement
  {
    const count_text = new UITextElement(
      ctx,
      m.count_x,
      m.count_y,
      m.count_w,
      m.count_h,
      {
        text,
        font_size: m.font_size,
        min_font_size: m.min_font_size,
        scale_to_width: true,
        auto_wrap: false,
        font_weight: 900,
        text_color: ColorScheme.menu_top_el_text,
        text_style: TextStyleScheme.geologica,
      }
    );
    count_text.setClickable( false );
    return count_text;
  }
}

export interface CoinCounterPillOptions
{
  /** The green "+" on the coin (default true). The shop has none: it is the shop. */
  plus?: boolean;
  /** Pressable as one widget, icon included (the menu's shortcut to the shop). */
  clickable?: boolean;
}

/**
 * The coin counter: pill, coin icon with "+", and the balance. The coin is a DOM
 * child of the pill, so the two always appear, move and fade together.
 *
 * (x, y) is the top-left of the pill BACKGROUND in the parent's coordinates; the
 * element itself is the slightly larger box that also covers the icon.
 */
export class CoinCounterPill extends UIElementHolder
{
  public readonly bg: UINineSliceElement;
  public readonly icon: UIImage;
  public readonly plus: UIImage | null;
  public readonly count_text: UITextElement;

  private shown_coins: number;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, options: CoinCounterPillOptions = {} )
  {
    const m = TopCounterLayout.metrics();
    super( ctx, x + m.holder_left, y + m.holder_top, m.holder_w, m.holder_h, false, options.clickable === true );
    this.setClickable( options.clickable === true );

    this.shown_coins = StatisticsStorage.load().coins;

    this.bg = new UINineSliceElement( ctx, -m.holder_left, -m.holder_top, m.bg_w, m.bg_h, ImgID.UI_MenuBlockBg, "menu_top_bg" );
    this.bg.setClickable( false );
    this.addChild( this.bg );

    this.count_text = TopCounterLayout.createCountText( ctx, m, formatCoinCount( this.shown_coins ) );
    this.bg.addChild( this.count_text );

    this.icon = new UIImage( ctx, m.icon_x, m.icon_y, m.icon_w, m.icon_h, ImgID.IconCoin, {}, false );
    this.icon.setClickable( false );
    this.bg.addChild( this.icon );

    this.plus = null;
    if ( options.plus !== false )
    {
      this.plus = new UIImage( ctx, m.plus_x, m.plus_y, m.plus_w, m.plus_h, ImgID.IconPlus, {}, false );
      this.plus.setClickable( false );
      this.icon.addChild( this.plus );
    }
  }

  public name(): string { return "CoinCounterPill"; }

  public getShownCoins(): number
  {
    return this.shown_coins;
  }

  /** Returns true when the text changed. */
  public setCoins( coins: number ): boolean
  {
    if ( coins === this.shown_coins )
      return false;

    this.shown_coins = coins;
    this.count_text.setText( formatCoinCount( coins ) );
    return true;
  }

  public syncCoins(): boolean
  {
    return this.setCoins( StatisticsStorage.load().coins );
  }

  /** Centre of the coin icon in screen coordinates. */
  public iconCenterAbsolute(): Vector2
  {
    const pos = this.icon.getAbsolutePosition();
    const rect = this.icon.getRect();
    return new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 );
  }
}
